import { format } from 'date-fns';
import { enGB } from 'date-fns/locale/en-GB';
import { sl } from 'date-fns/locale/sl';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderDto } from '../../../../api-types';
import i18n from '../../../../i18n';
import { getOrdersPrice } from '../../../../utils/calculateOrdersPrice';
import { Months } from '../months';
import { Weeks } from '../weeks';

import './index.css';
import { useAppDispatch } from '../../../../app/hooks';
import { setError } from '../../../../app/commonSlice';
import { BookingSlot } from '../../../../api-types/models/Booking';
import { ApiError } from '../../../../api/errors';
import { useGetBookingsForActiveMonthQuery } from '../../../../api/bookingApi';
import { useGetOrdersForMonthQuery } from '../../../../api/ordersApi';
import { MenuItemType } from '../../../../api-types/models/MenuItemDto';

export function Calendar(): React.ReactNode {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [activeMonthIndex, setActiveMonthIndex] = useState<number>();

    const currentMonthIndex = new Date().getMonth();

    useEffect(() => {
        setActiveMonthIndex(currentMonthIndex);
    }, []);

    const activeYear =
        activeMonthIndex !== undefined && currentMonthIndex < activeMonthIndex
            ? new Date().getFullYear() - 1
            : new Date().getFullYear();

    const monthDate = useMemo(() => {
        if (activeMonthIndex === undefined) {
            return '';
        }

        return `${activeYear}-${String(activeMonthIndex + 1).padStart(2, '0')}-01`;
    }, [activeMonthIndex, activeYear]);

    const { data: bookingsForThisMonth, error: bookingsError } =
        useGetBookingsForActiveMonthQuery(monthDate, {
            skip: activeMonthIndex === undefined
        });

    const { data: ordersForThisMonth, error: ordersError } =
        useGetOrdersForMonthQuery(
            { month: activeMonthIndex ?? 0, year: activeYear },
            { skip: activeMonthIndex === undefined }
        );

    useEffect(() => {
        if (bookingsError && bookingsError instanceof ApiError) {
            dispatch(setError(bookingsError));
        }
    }, [bookingsError, dispatch]);

    useEffect(() => {
        if (ordersError && ordersError instanceof ApiError) {
            dispatch(setError(ordersError));
        }
    }, [ordersError, dispatch]);

    if (activeMonthIndex === undefined) {
        return <></>;
    }

    return (
        <div className='calendar-container'>
            <div className='calendar-options'>
                <Months
                    activeMonthIndex={activeMonthIndex}
                    setActiveMonthIndex={setActiveMonthIndex}
                    currentMonthIndex={currentMonthIndex}
                />
                <div className='options-title price'>
                    {t('total')}{' '}
                    {format(
                        new Date(new Date().getFullYear(), activeMonthIndex, 1),
                        'LLLL',
                        { locale: i18n.language === 'en' ? enGB : sl }
                    )}{' '}
                    {ordersForThisMonth && getOrdersPrice(ordersForThisMonth)}
                    {' €'}
                </div>
            </div>

            <div className='calendar-wrapper'>
                <h3>{t('kitchen')}</h3>
                <div className='calendar-options'>
                    <Weeks<OrderDto>
                        activeMonthIndex={activeMonthIndex}
                        activeYear={activeYear}
                        elementsForThisMonth={ordersForThisMonth?.filter(
                            (order) =>
                                order.orderItems.some(
                                    (item) =>
                                        item.menuItem.costCenter ===
                                        MenuItemType.KITCHEN
                                )
                        )}
                    />
                </div>
                <h3>{t('bar')}</h3>
                <div className='calendar-options'>
                    <Weeks<OrderDto>
                        activeMonthIndex={activeMonthIndex}
                        activeYear={activeYear}
                        elementsForThisMonth={ordersForThisMonth?.filter(
                            (order) =>
                                order.orderItems.some(
                                    (item) =>
                                        item.menuItem.costCenter ===
                                        MenuItemType.BAR
                                )
                        )}
                    />
                </div>
                <h3>{t('activities')}</h3>
                <div className='calendar-options'>
                    <Weeks<BookingSlot>
                        activeMonthIndex={activeMonthIndex}
                        activeYear={activeYear}
                        elementsForThisMonth={bookingsForThisMonth}
                        booking={true}
                    />
                </div>
            </div>
        </div>
    );
}
